import { Button, Form, Input, Select, Space } from "antd";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { useOnFormClose, useGenericFormErrorNotification } from "@/hooks/forms";
import { useChain } from "@/hooks/useChain";
import { useFormValidation } from "@/modules/form-validation";
import OrganizationSelect from "@/modules/organization/organization-select";
import QueryableSelect from "@/modules/queryable-select";
import { SidePanelFooter } from "@/modules/side-panel";
import { Tag } from "@/ui/tag";

// TODO DEV-8551 `Option` is deprecated. Please use `options` instead.
// eslint-disable-next-line @typescript-eslint/no-deprecated
const { Option } = Select;
const { useForm } = Form;

const UserForm = ({ mutation, saveButtonLabel, initialValues, onMutate }) => {
  const [form] = useForm();
  const { t } = useTranslation();

  const onClose = useOnFormClose(form);
  const onMutateSuccess = useChain([onClose, onMutate]);

  useGenericFormErrorNotification(mutation.isError);
  const { validationErrors, validation } = useFormValidation(
    mutation.isError,
    mutation.error,
    ["name", "email", "organization_id", "role"]
  );

  const onSave = (formData) => {
    const data = {
      name: formData.name,
      email: formData.email,
      organization_id: formData.organization.value,
      role: formData.role.value ?? formData.role,
    };

    mutation.mutate(data, { onSuccess: onMutateSuccess });
  };

  const roleOptionFormat = ({ option: role }) => {
    if (role) {
      // display only main roles
      if (role.includes("SuperAdmin") || role.includes("Organization")) {
        return (
          <Option key={role} value={role}>
            <Tag>
              {t("role:" + role.split("\\").slice(-1)[0].toLowerCase())}
            </Tag>
          </Option>
        );
      }
    }
  };

  return (
    <Form
      id="userForm"
      form={form}
      layout="vertical"
      initialValues={initialValues}
      onFinish={onSave}
      role="form"
    >
      <Form.Item
        name="name"
        label={t("user:form.name.label")}
        rules={[{ required: true }]}
        {...validation("name")}
      >
        <Input placeholder={t("user:form.name.label")} data-testid="name" />
      </Form.Item>
      <Form.Item
        name="email"
        label={t("user:form.email.label")}
        rules={[{ required: true }]}
        {...validation("email")}
      >
        <Input placeholder={t("user:form.email.label")} data-testid="email" />
      </Form.Item>
      <OrganizationSelect {...validation("organization_id")} />
      <QueryableSelect
        model="behaviors"
        filterBy="name"
        dataTestId="roleSelect"
        placeholder={t("user:form.mainRole.label")}
        name="role"
        label={t("user:form.mainRole.label")}
        rules={[{ required: true }]}
        optionFormat={roleOptionFormat}
        {...validation("role")}
      />

      {validationErrors().others && <Form.Item {...validation("others")} />}

      <SidePanelFooter>
        <Space>
          <Button onClick={onClose}>{t("global:cancel")}</Button>
          <Button
            loading={mutation.isPending}
            type="primary"
            htmlType="submit"
            form="userForm"
          >
            {saveButtonLabel}
          </Button>
        </Space>
      </SidePanelFooter>
    </Form>
  );
};

const modelShape = PropTypes.shape({
  id: PropTypes.number,
  name: PropTypes.string,
  email: PropTypes.string,
  organization_id: PropTypes.number,
  role: PropTypes.string,
});

const initialValuesShape = PropTypes.shape({
  name: PropTypes.string,
  email: PropTypes.string,
  organization_id: PropTypes.number,
  role: PropTypes.string,
});

UserForm.propTypes = {
  model: modelShape,
  mutation: PropTypes.object.isRequired,
  saveButtonLabel: PropTypes.string.isRequired,
  initialValues: initialValuesShape.isRequired,
  onMutate: PropTypes.func,
};

export default UserForm;
